import { useState, useEffect } from 'react'
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { Box, Stepper, Step, StepLabel } from '@mui/material'
import { snsMemberCheck, phoneMemberCheck, submitSignUp, uploadDataInfo } from "@api/member";
import { emailSearch } from "@api/login";
import { useConfirm } from '@components/ConfirmProvider'
import { useIsMobile } from '@/hooks/Responsive'

import Step0 from './Step0'
import Step1 from './Step1'
import Step2 from './Step2'
import Step3 from './Step3'
import Step4 from './Step4'
import Step5 from './Step5'

const steps = ['회원유형선택', '약관동의', '본인인증', '회원정보입력', '가입완료']
const { REACT_APP_API_BASE } = process.env;

// 회원유형
const MemberType = {
  GNRL : "GNRL", // 일반학습자 : General
  INST: "INST", // 기관학습자 : Institute
};

// 회원종류
const MemberKind = {
  G14U: "G14U", // 14세 이상 일반학습자 : General 14 up
  G14D: "G14D", // 14세 미만 일반학습자 (보호자 인증) : General 14 down
  FRNR: "FRNR", // 국내거주 외국인 : Foreigner
  
  KKO: "kakao", // 간편회원가입 : kakao
  NVR: "naver", // 간편회원가입 : naver
  GGL: "google", // 간편회원가입 : google
};

// 단계구분
const StepIndex = {
  TYPE0: 0,  // 회원유형선택
  KIND1: 1,  // 회원종류 선택
  TERM2: 2,  // 약관동의
  VERI3: 3,  // 본인인증/보호자인증
  FORM4: 4,  // 회원정보 입력
  DONE5: 5,  // 완료 (또는 기관승인 안내)
};


export default function SignupStepper() {
  const { isMobile } = useIsMobile()
  const { alert, confirm } = useConfirm();

  const [step, setStep] = useState(StepIndex.TYPE0);  // 단계구분
  const [type, setType] = useState(null);  // 회원유형
  const [kind, setKind] = useState(null);  // 회원종류
  const [terms, setTerms] = useState({ agree1: "", agree2: "" });  // [2단계]약관동의정보
  const [verified, setVerified] = useState(null);  // [3단계]본인인증정보
  const [profile, setProfile] = useState(null);  // 간편회원가입(sns)정보
  const [memberInfo, setMemberInfo] = useState(null);  // [4단계]회원정보
  const [tempMemberInfo, setTempMemberInfo] = useState(null);  // [4단계]임시회원정보

  const navigate = useNavigate();

  const handleNext = () => setStep((prev) => prev + 1)
  const handleBack = () => setStep((prev) => prev - 1)

  //============================================================================
  // step 이동 시 scrolltop 0
  useEffect(() => {
    requestAnimationFrame(() => {
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }, 0)
    })
  }, [step])

  // 단계 화면표시
  const getStepContent = (step) => {
    switch (step) {
      case 0: return  <Step0 
                        onNext={(k) => {step0OnNext(k)}}
                        MemberType={MemberType}
                      />
      case 1: return  <Step1 
                        onNext={(k) => { setKind(k); setStep(StepIndex.TERM2); }}
                        MemberKind={MemberKind}
                        snsAuth={snsAuth}
                      />
      case 2: return  <Step2 
                        onNext={() => setStep(StepIndex.VERI3)}
                        terms={terms}
                        setTerms={setTerms}
                        reset={reset}
                        onBack={handleBack}
                      />
      case 3: return  <Step3 
                        type={type}
                        MemberKind={MemberKind}
                        kind={kind}
                        terms={terms}
                        profile={profile}
                        onVerified={onVerified}
                        tempMemberInfo={tempMemberInfo}
                      />
      case 4: return  <Step4 
                        MemberType={MemberType}
                        type={type}
                        kind={kind}
                        verified={verified}
                        onSubmit={handleSubmit}
                        reset={reset}
                        tempMemberInfo={tempMemberInfo}
                        MemberKind={MemberKind}
                      />
      case 5: return  <Step5 
                        MemberType={MemberType}
                        type={type}
                        memberInfo={memberInfo}
                        onGoHome={reset}
                      />
      default: return null
    }
  }

  // 데이터 초기화
  const reset = () => {
    // 기관인경우 메인으로 일반인경우 로그인으로
    const isInstitute = type === MemberType.INST;

    setStep(StepIndex.TYPE0);
    setType(null);
    setKind(null);
    setTerms({ agree1:"", agree2:"" });
    setVerified(null);
    setProfile(null);
    setMemberInfo(null);

    // isInstitute ? navigate("/") : navigate("/Login");
    // 정의 회원가입취소시 정의 안되서 일단 메인으로..
    navigate("/");
  };



  //============================================================================
  //============================================================================
  // [0단계] 임시회원 체크
  const step0OnNext = (memberTypeData) => {
    if (tempMemberInfo) {
      if (memberTypeData == MemberType.GNRL) {
        alert('임시회원은 기관학습자로만 가입이 가능합니다.');
      }
      else {
        setType(memberTypeData);
        setStep(StepIndex.KIND1);
      }
    }
    else {
      setType(memberTypeData);
      setStep(StepIndex.KIND1);
    }
  };



  //============================================================================
  //============================================================================
  // [1단계] 간편회원가입 호출
  const snsAuth = (provider) => {
    setKind(provider);
    localStorage.setItem("MemberRegist.type", type);
    localStorage.setItem("MemberRegist.kind", provider);
    
    // 임시회원 링크로 접속한 경우
    if (tempMemberInfo) {
      localStorage.setItem("callBackUrl", "/signup?uploadId=" + tempMemberInfo.uploadId);
    }
    else {
      localStorage.setItem("callBackUrl", "/signup");
    }

    window.location.href = `${REACT_APP_API_BASE}/oauth2/authorization/${provider}`;
  };

  const location = useLocation();
  const isFromSocial = location.state?.fromSocial === true;  // sns관련 콜백
  
  // 링크url 관련
  const [searchParams] = useSearchParams();
  const uploadId = searchParams.get("uploadId");
  const isUploadId = uploadId ? true : false;

  // [1단계] 소셜(SNS) 로그인 콜백 처리
  useEffect(() => {

    // ===========================
    // sns 관련
    if (isFromSocial) {
      const returnType = localStorage.getItem("MemberRegist.type");
      const returnKind = localStorage.getItem("MemberRegist.kind");

      // 데이터 재설정
      setType(returnType);
      setKind(returnKind);

      const stored = localStorage.getItem("socialProfile");
      if (stored) {
        try {
          const socialProfile = JSON.parse(stored);

          // 프로필 저장
          setProfile(socialProfile);

          // 간편연계(sns) 회원체크
          memberCheck(returnType, returnKind, socialProfile);
        }
        catch (e) {
          console.error(e);
        }
        finally {
          // localStorage 등록한 데이터 제거
          ["MemberRegist.type", "MemberRegist.kind", "socialProfile"].forEach((k) =>
            localStorage.removeItem(k)
          );
        }
      }
    }

    // ===========================
    // ===========================
    // 임시회원 링크url 관련
    if (isUploadId) {
      // csv파일로 등록된 정보로 회원가입 체크
      uploadDataCheck(uploadId);
    }

    // ===========================

  }, []);

  // [1단계] 간편연계(sns) 회원체크
  const memberCheck = async (returnType, returnKind, socialProfile) => {

    // sns id 추출
    const snsNaverKey = returnKind == 'naver' ? socialProfile?.response?.id : '';
    const snsKakaoKey = returnKind == 'kakao' ? socialProfile?.id : '';
    const snsGoogleKey = returnKind == 'google' ? socialProfile?.sub : '';

    const params = {
      memberTypeCode : '',  // returnType
      memberKindCode : returnKind,
      snsNaverKey : snsNaverKey,
      snsKakaoKey : snsKakaoKey,
      snsGoogleKey : snsGoogleKey,
    };

    try {
      const res = await snsMemberCheck(params);
      if (res?.success) {
        // 기존에 등록된 회원이 없는 경우
        if (res?.data?.registedMemberYn == 'N') {
          // 바로 2단계(약관동의)로 이동
          setStep(StepIndex.TERM2);
        }
        // 기존에 등록된 회원이 있는 경우
        else {
          alert('선택한 간편연계에 등록된 회원정보가 있습니다.');
        }
      }
      else {
        throw new Error("가입 처리에 실패했습니다.");
      }
    }
    catch (e) {
      alert(e?.response?.data?.message || e.message || "가입 중 오류가 발생했습니다.");
    }
    finally {
      // 처리 후 플래그 제거
      navigate(".", { replace: true, state: undefined });
    }
  };

  // [1단계] csv파일로 등록된 정보로 회원가입 체크
  const uploadDataCheck = async (uploadId) => {
    const params = {
      encUploadId: uploadId,
    };

    try {
      const res = await uploadDataInfo(params);
      if (res?.success) {
        // 성별체크
        let sex = res?.data?.sex;
        if (sex === "남자" || sex === "M") sex = "M";
        else if (sex === "여자" || sex === "F") sex = "F";

        // 기존에 등록된 임시회원이 있는 경우
        setTempMemberInfo(
          {
            uploadId: uploadId,
            name: res?.data?.memberName,
            sex: sex,
            birthDay: res?.data?.birthdate.slice(0, 4),  // 앞4자리만
            nationality: res?.data?.nationalType,
            email: res?.data?.memberEmail,
            phone: res?.data?.cellphoneNo,
            instCode: res?.data?.memberInstCode,
            instName: res?.data?.memberInstName,
          }
        );
      }
      else {
        throw new Error("등록된 임시회원정보가 없습니다.");
      }
    }
    catch (e) {
      alert(e?.response?.data?.message || e.message || "가입 중 오류가 발생했습니다.");
    }
  };

  // 데이터 확인용
  useEffect(() => {
    //console.log('👉 tempMemberInfo = ' + JSON.stringify(tempMemberInfo, null, 2));
  }, [tempMemberInfo]);



  //============================================================================
  //============================================================================
  const isFromPortone = location.state?.fromPortone === true;  // 본인인증관련 콜백
  const [portonePayload, setPortonePayload] = useState(null);

  // [3단계][모바일]PortOne(휴대전화 본인인증) 콜백 처리
  useEffect(() => {
    if (!isFromPortone) return;

    const stored = localStorage.getItem("PortOne.Signup.verified");
    if (!stored) {
      // state 초기화 (새로고침 시 다시 콜백 처리 안 되도록)
      navigate(".", { replace: true, state: undefined });
      return;
    }

    try {
      const info = JSON.parse(stored);

      const callbackStep = Number(localStorage.getItem("Step3.step")); // 숫자로 변환
      const callbackType = localStorage.getItem("Step3.type");
      const callbackKind = localStorage.getItem("Step3.kind");
      const callbackTerms = JSON.parse(localStorage.getItem("Step3.terms"));
      const callbackProfile = JSON.parse(localStorage.getItem("Step3.profile"));
      const callbackTempMemberInfo = JSON.parse(localStorage.getItem("Step3.tempMemberInfo"));
      
      const childName = localStorage.getItem("Step3.childName");
      const childBirthYear = localStorage.getItem("Step3.childBirthYear");
      const childGender = localStorage.getItem("Step3.childGender");

      // 기존데이터 등록
      setStep(callbackStep);
      setType(callbackType);
      setKind(callbackKind);
      setTerms(callbackTerms);
      setProfile(callbackProfile);
      setTempMemberInfo(callbackTempMemberInfo);

      // doPortOneMobile에 쓸 payload만 따로 저장
      setPortonePayload({
        info,
        callbackStep,
        callbackType,
        callbackKind,
        callbackTerms,
        callbackProfile,
        callbackTempMemberInfo,
        childName,
        childBirthYear,
        childGender,
      });

    }
    catch (e) {
      console.error(e);
    }
    finally {
      // localStorage 등록한 데이터 제거
      [
        "Step3.step",
        "Step3.type",
        "Step3.kind",
        "Step3.terms",
        "Step3.profile",
        "Step3.tempMemberInfo",
        "PortOne.Signup.verified",
        "Step3.childName",
        "Step3.childBirthYear",
        "Step3.childGender",
      ].forEach((k) => localStorage.removeItem(k));
    }

    // state 초기화 (새로고침 시 다시 콜백 처리 안 되도록)
    navigate(".", { replace: true, state: undefined });

  }, [isFromPortone, navigate]);

  useEffect(() => {
    if (!portonePayload) return;

    // ✅ 이 시점은 위 setState들이 반영된 다음 렌더 이후라 안전
    doPortOneMobile(
      portonePayload.info,
      portonePayload.callbackType,
      portonePayload.callbackKind,
      portonePayload.callbackTempMemberInfo,
      portonePayload.childName,
      portonePayload.childBirthYear,
      portonePayload.childGender
    );

    // 1회 실행 후 정리
    setPortonePayload(null);

  }, [portonePayload]);
  
  // [모바일]PortOne인증 콜백 함수
  const doPortOneMobile = (data, callbackType, callbackKind, callbackTempMemberInfo, childName, childBirthYear, childGender) => {
    if (data?.status == 'VERIFIED') {
      // 14세미만 회원인경우
      if (callbackKind == MemberKind.G14D) {
        doPortOneSubmit(data, callbackType, callbackKind, childName, childBirthYear, childGender);
      }
      // 14세미만제외 회원인 경우
      else {
        const currentTempMemberInfo = callbackTempMemberInfo ?? tempMemberInfo;

        // 임시회원인 경우
        if (currentTempMemberInfo) {
          // 임시회원정보와 일치하지 않는 경우(이름, 출생연도, 전화번호)
          if (
            currentTempMemberInfo.name !== data?.name ||
            currentTempMemberInfo.birthDay !== data?.birthDate.slice(0, 4) ||
            currentTempMemberInfo.phone !== data?.phone
          ) {
            alert('등록된 임시회원정보와 일치하지 않습니다.');
          }
          else {
            doPortOneSubmit(data, callbackType, callbackKind, childName, childBirthYear, childGender);
          }
        }
        // 신규회원 가입인 경우
        else {
          doPortOneSubmit(data, callbackType, callbackKind, childName, childBirthYear, childGender);
        }
      }
    }
    else {
      alert("본인인증을 실패했습니다.");
    }
  };

  // doPortOne return
  const doPortOneSubmit = (data, callbackType, callbackKind, childName, childBirthYear, childGender) => {
    onVerified(
      {
        name: callbackKind === MemberKind.G14D ? childName : data?.name,
        birthDay: callbackKind === MemberKind.G14D ? childBirthYear : data?.birthDate.slice(0, 4),  // 앞4자리만
        gender: callbackKind === MemberKind.G14D ? childGender : data?.gender.slice(0, 1),  // 앞1자리만
        phone: data?.phone,
        nationality: data?.foreigner == 'true' ? "FOREIGNER" : "LOCAL",
        certToken: data?.certToken,
      }, callbackType
    );
  };

  // [3단계]본인인증 결과 후처리 : 본인인증 PC와 모바일 모두 사용
  const onVerified = async (info, callbackType) => {
    setVerified(info);

    const currentType = callbackType ?? type;

    const params = {
      memberTypeCode : '',  // type
      memberKindCode : '',
      memberName : info.name,
      birthdate : info.birthDay,
      cellphoneNo : info.phone,
    };

    try {
      // const res = await phoneMemberCheck(params);  //기존
      const res = await emailSearch(params);
      
      if (res?.success) {
        const list = res.data || [];
        const emailsText = 
          list.length > 0 ? list.map(item => item.memberEmail).join(',\n') : '';
        
        // 기존에 등록된 회원이 없는 경우
        if (res?.data.length == 0) {
          setStep(StepIndex.FORM4);
        }
        // 기존에 등록된 회원이 있는 경우
        else {
          // 기관학습자로 회원가입인 경우
          if (currentType == MemberType.INST) {
            // 임시회원 링크로 들어온 경우
            if (tempMemberInfo) {
              const tempEmail = tempMemberInfo?.email;
              // 임시회원 이메일이 없는 경우
              if (!tempEmail) {
                //console.log("임시회원 이메일이 없습니다.");
              }
              // 임시회원 이메일이 있는 경우
              else {
                const isMatched = list.some(item => item.orgMemberEmail === tempEmail);
                // 회원등록된 이메일과 일치하는 데이터가 있는 경우
                if (isMatched) {
                  alert('이미 등록된 사용자입니다.\n로그인 후 등록할 기관정보을 확인해 주세요.');
                }
                // 회원등록된 이메일과 일치하는 데이터가 없는 경우
                else {
                  const proceed = await confirm({
                    title: '추가등록안내',
                    message: emailsText + '\n으로 회원가입이 되어 있습니다.\n추가 계정을 등록하시겠습니까?',
                    confirmText: '추가등록',
                    hideCancel: true,
                  });
                  if (proceed) {
                    setStep(StepIndex.FORM4);
                  }
                }
              }
            }
            // 신규회원으로 들어온 경우
            else {
              const proceed = await confirm({
                title: '추가등록안내',
                message: emailsText + '\n으로 회원가입이 되어 있습니다.\n추가 계정을 등록하시겠습니까?',
                confirmText: '추가등록',
                hideCancel: true,
              });
              if (proceed) {
                setStep(StepIndex.FORM4);
              }
            }
          }
          // 일반학습자로 회원가입인 경우
          else {
            alert('이미 등록된 사용자입니다.\n아이디/비밀번호 찾기를 해 주세요.');
          }
        }
      }
      else {
        throw new Error("본인인증 확인에 실패했습니다.");
      }
    }
    catch (e) {
      alert(e?.response?.data?.message || e.message || "본인인증 확인중 오류가 발생했습니다.");
    }
    finally {
      // localStorage 등록한 데이터 제거
      [
        "Step3.step",
        "Step3.type",
        "Step3.kind",
        "Step3.terms",
        "Step3.profile",
        "Step3.tempMemberInfo",
        "PortOne.Signup.verified",
        "Step3.childName",
        "Step3.childBirthYear",
        "Step3.childGender",
      ].forEach((k) => localStorage.removeItem(k));
    }

  };



  //============================================================================
  //============================================================================
  // [4단계]회원정보 등록
  const handleSubmit = async (memberInfo) => {

    // 최종등록 데이터
    setMemberInfo(memberInfo);

    // sns id 추출
    const snsNaverKey = memberInfo.kind == 'naver' ? profile?.response?.id : '';
    const snsKakaoKey = memberInfo.kind == 'kakao' ? profile?.id : '';
    const snsGoogleKey = memberInfo.kind == 'google' ? profile?.sub : '';

    // 동의체크 확인
    const requiredOk = terms.agree1 === 'agree';

    const params = {
      memberEmail : memberInfo.email,
      memberName : memberInfo.name,
      memberPassword : memberInfo.password,
      memberSiteCode : 'PORTAL',
      memberKindCode : memberInfo.kind.toUpperCase(),
      memberTypeCode : memberInfo.type,
      memberInstCode : memberInfo.instCode,
      memberClssCode : memberInfo.instClass,
      birthdate : memberInfo.birthDay,
      sex : memberInfo.gender,
      cellphoneNo : memberInfo.phone,
      nationalType : memberInfo.nationality,
      interestContent : memberInfo.content,
      interestCourse : memberInfo.course,
      indvInfoAgreeYn : requiredOk ? 'Y' : 'N',
      snsNaverKey : snsNaverKey,
      snsKakaoKey : snsKakaoKey,
      snsGoogleKey : snsGoogleKey,
      snsToken : sessionStorage.getItem("snsToken") || "",
      certToken : memberInfo.certToken || "",
      permissionYn : type === MemberType.INST ? 'I' : '',
    };

    try {
      const res = await submitSignUp(params);
      if (res?.success) {
        sessionStorage.removeItem("snsToken");
        setStep(StepIndex.DONE5);
      }
      else {
        throw new Error("가입 처리에 실패했습니다.");
      }
    }
    catch (e) {
      alert(e?.response?.data?.message || e.message || "가입 중 오류가 발생했습니다.");
    }
    finally {
      //console.log('');
    }

  };



  //============================================================================



  function CustomStepIcon(props) {
    const { active, completed, icon } = props;
    const isActive = active || completed;
    return (
      <Box
        sx={{
          width: '32px',
          height: '32px',
          borderRadius: '50%',
          bgcolor: isActive ? 'primary.main' : 'grey.50',
          color: isActive ? 'text.contrastText' : 'grey.500',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          typography: 'body2',
          fontWeight: '600',
        }}
      >
        {String(icon).padStart(2, '0')}
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 6 }}>
      {step !== 0 && (
        <Stepper
          activeStep={step - 1}
          orientation="horizontal"
          sx={{
            '& .MuiStep-root': {
              px: isMobile ? 0.5 : 1,
              minWidth: 'auto'
            },
            '& .MuiStepConnector-root': {
              display: 'none',
            },

            '& .MuiStepLabel-label': {
              display: 'none',
            },

            '& .MuiStepLabel-label.Mui-active': {
              display: 'block !important',
              typography: 'body2',
              fontWeight: '500',
            },
          }}
        >
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel StepIconComponent={CustomStepIcon}>
              {label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>
      )}
      <Box sx={{ mt: 6 }}>{getStepContent(step)}</Box>
    </Box>
  )
}
